import { getSearchQuery } from '@codemirror/search';
import { EditorView, ViewPlugin, type ViewUpdate } from '@codemirror/view';
import { alpha } from '@mui/material/styles';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
  YELLOW,
} from 'styles/tokens';

/**
 * Shared CodeMirror 6 styling for the Relif editor family. Tonal surface, Navy text,
 * inset focus ring (no 1px borders), and a glassmorphism search/replace panel with a
 * VS Code-style match counter. Consumed by the generic `CodeMirrorEditor` base and any
 * editor built on it (prompt, JSON). All values come from `styles/tokens`.
 */

// VS Code-style match counter ("3/17") shown next to the search field. The default
// @codemirror/search panel has no count, so we inject a span into it and keep it updated
// with the current query's match total and the selected index.
function countMatches(view: EditorView): { current: number; total: number } | null {
  const query = getSearchQuery(view.state);
  if (!query.search || !query.valid) return null;
  const sel = view.state.selection.main;
  let total = 0;
  let current = 0;
  try {
    const cursor = query.getCursor(view.state.doc);
    let res = cursor.next();
    while (!res.done) {
      total += 1;
      const { from, to } = res.value;
      if (from === sel.from && to === sel.to) current = total;
      res = cursor.next();
    }
  } catch {
    return null; // invalid regexp mid-typing
  }
  return { current, total };
}

export const searchMatchCounter = ViewPlugin.fromClass(
  class {
    el: HTMLSpanElement | null = null;

    constructor(view: EditorView) {
      this.render(view);
    }

    update(update: ViewUpdate) {
      this.render(update.view);
    }

    render(view: EditorView) {
      const panel = view.dom.querySelector('.cm-panel.cm-search');
      if (!panel) {
        this.el?.remove();
        this.el = null;
        return;
      }
      if (!this.el || !panel.contains(this.el)) {
        this.el = document.createElement('span');
        this.el.className = 'cm-search-count';
        const input = panel.querySelector('input[name="search"]');
        if (input) input.insertAdjacentElement('afterend', this.el);
        else panel.appendChild(this.el);
      }
      const counts = countMatches(view);
      if (!counts) this.el.textContent = '';
      else if (counts.total === 0) this.el.textContent = 'Sin resultados';
      else if (counts.current > 0) this.el.textContent = `${counts.current}/${counts.total}`;
      else this.el.textContent = `${counts.total} coincidencias`;
    }

    destroy() {
      this.el?.remove();
      this.el = null;
    }
  }
);

export const editorTheme = EditorView.theme(
  {
    '&': {
      backgroundColor: SURFACE_CONTAINER_LOW,
      color: NAVY,
      borderRadius: RADIUS_ITEM,
      fontSize: '13px',
      height: '100%',
      overflow: 'hidden',
      // Subtle resting inset ring (not a 1px border) so the field reads as an editor on
      // near-white backgrounds; deepens on focus below.
      boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.07)}`,
    },
    '&.cm-focused': {
      outline: 'none',
      boxShadow: `inset 0 0 0 2px ${alpha(NAVY, 0.18)}`,
    },
    '.cm-content': {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      caretColor: NAVY,
      padding: '10px 14px',
    },
    '.cm-scroller': { overflow: 'auto' },
    '.cm-gutters': {
      backgroundColor: 'transparent',
      color: alpha(NAVY, 0.4),
      border: 'none',
      paddingLeft: '6px',
    },
    '.cm-lineNumbers .cm-gutterElement': { padding: '0 8px 0 6px' },
    '.cm-activeLineGutter': { backgroundColor: SURFACE_HOVER, color: NAVY },
    '.cm-activeLine': { backgroundColor: alpha(NAVY, 0.02) },
    '.cm-cursor': { borderLeftColor: NAVY },
    '.cm-selectionBackground, &.cm-focused .cm-selectionBackground, ::selection': {
      backgroundColor: alpha(NAVY, 0.12),
    },
    '.cm-searchMatch': { backgroundColor: alpha(YELLOW, 0.4), borderRadius: '2px' },
    '.cm-searchMatch-selected': { backgroundColor: alpha(YELLOW, 0.7) },

    // ── Search/replace panel (Cmd/Ctrl+F) — VS Code-style floating widget, Relif palette.
    '.cm-panels': {
      backgroundColor: 'transparent',
      color: NAVY,
      zIndex: 12,
    },
    // Float the panel top-right over the content instead of pushing it down.
    '.cm-panels.cm-panels-top': {
      position: 'absolute',
      top: '8px',
      right: '14px',
      left: 'auto',
      width: 'auto',
      maxWidth: 'calc(100% - 28px)',
      borderBottom: 'none',
    },
    '.cm-panel.cm-search': {
      position: 'relative',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '6px',
      padding: '10px 34px 10px 12px',
      background: GLASS_BACKGROUND,
      backdropFilter: GLASS_BLUR,
      WebkitBackdropFilter: GLASS_BLUR,
      borderRadius: RADIUS_CARD,
      boxShadow: SHADOW,
      fontFamily: 'Jost, sans-serif',
      fontSize: '12.5px',
    },
    // Force a row break before the "replace" row (the default <br> in a flex row).
    '.cm-panel.cm-search br': { flexBasis: '100%', width: 0, height: 0, margin: 0, border: 'none' },
    '.cm-panel.cm-search .cm-textfield': {
      fontFamily: 'inherit',
      fontSize: '12.5px',
      color: NAVY,
      backgroundColor: SURFACE_CONTAINER_LOWEST,
      border: 'none',
      borderRadius: RADIUS_ITEM,
      padding: '5px 9px',
      outline: 'none',
      minWidth: '170px',
      boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.06)}`,
      transition: 'box-shadow 0.15s ease',
    },
    '.cm-panel.cm-search .cm-textfield:focus': {
      boxShadow: `inset 0 0 0 2px ${alpha(NAVY, 0.18)}`,
    },
    '.cm-panel.cm-search .cm-textfield::placeholder': { color: alpha(NAVY, 0.4) },
    '.cm-panel.cm-search .cm-button': {
      fontFamily: 'inherit',
      fontSize: '12px',
      color: NAVY,
      backgroundColor: SURFACE_CONTAINER_LOW,
      backgroundImage: 'none',
      border: 'none',
      borderRadius: RADIUS_ITEM,
      padding: '5px 11px',
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
    },
    '.cm-panel.cm-search .cm-button:hover': { backgroundColor: SURFACE_HOVER },
    '.cm-panel.cm-search .cm-button:active': { backgroundColor: alpha(NAVY, 0.12) },
    '.cm-panel.cm-search label': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontFamily: 'inherit',
      fontSize: '12px',
      color: alpha(NAVY, 0.75),
    },
    '.cm-panel.cm-search input[type=checkbox]': { accentColor: NAVY, margin: 0 },
    '.cm-panel.cm-search .cm-search-count': {
      fontFamily: 'Jost, sans-serif',
      fontSize: '11.5px',
      fontVariantNumeric: 'tabular-nums',
      color: alpha(NAVY, 0.55),
      whiteSpace: 'nowrap',
      alignSelf: 'center',
      padding: '0 2px',
    },
    '.cm-panel.cm-search button[name=close]': {
      position: 'absolute',
      top: '8px',
      right: '8px',
      width: '22px',
      height: '22px',
      padding: 0,
      fontSize: '18px',
      lineHeight: 1,
      color: alpha(NAVY, 0.55),
      backgroundColor: 'transparent',
      borderRadius: RADIUS_ITEM,
      cursor: 'pointer',
    },
    '.cm-panel.cm-search button[name=close]:hover': { backgroundColor: SURFACE_HOVER, color: NAVY },
  },
  { dark: false }
);
